import { useState, type FormEvent } from 'react'
import { LockKeyhole } from 'lucide-react'

import { useDeletePhoneLock, usePhoneLock, usePhoneUnlock, useSavePhoneLock } from '@/api/endpoints/settings'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { errorMessage } from '@/lib/errors'
import { phoneLockSummary, PIN_PATTERN } from './phoneLock'
import styles from './PhoneLockPanel.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

/**
 * "Bloqueio de tela": o PIN que a automação digita para destravar o celular.
 * A API só grava e nunca devolve o PIN; os campos se esvaziam no envio. Um PIN
 * novo só vale para a automação depois de um "Desbloquear agora" que deu
 * certo, com o dono olhando o aparelho. Só o admin vê a aba.
 */
export function PhoneLockPanel() {
  const status = usePhoneLock()
  const save = useSavePhoneLock()
  const remove = useDeletePhoneLock()
  const unlock = usePhoneUnlock()
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const lock = status.data
  const summary = lock ? phoneLockSummary(lock) : null
  const stopped = lock ? lock.failures >= lock.max_failures : false
  const canSave = PIN_PATTERN.test(pin) && pin === confirm && !save.isPending

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const body = { pin, pin_confirm: confirm }
    setPin('')
    setConfirm('')
    unlock.reset()
    save.mutate(body, {
      onSuccess: () => {
        save.reset()
      },
    })
  }

  return (
    <div className={styles.panel}>
      <span className={styles.title}>Bloqueio de tela</span>
      {status.isError ? (
        <QueryErrorNotice
          compact
          error={status.error}
          label="o PIN do celular"
          onRetry={() => {
            void status.refetch()
          }}
        />
      ) : null}
      {lock && !lock.key_configured ? (
        <span className={styles.warn}>
          Falta a <code>PHONE_UNLOCK_KEY</code> no <code>.env</code> deste servidor: sem ela o PIN não é guardado.
        </span>
      ) : null}
      {summary ? (
        <span className={styles[summary.tone]} role="status">
          {summary.text}
        </span>
      ) : null}

      <form className={styles.form} onSubmit={submit} autoComplete="off">
        <Input
          className={styles.pin}
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={16}
          placeholder="PIN (6 a 16 dígitos)"
          aria-label="PIN do celular"
          value={pin}
          disabled={!lock?.key_configured}
          onChange={(event) => {
            setPin(event.target.value.replace(/\D/g, ''))
          }}
        />
        <Input
          className={styles.pin}
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={16}
          placeholder="Repita o PIN"
          aria-label="Repita o PIN do celular"
          value={confirm}
          disabled={!lock?.key_configured}
          onChange={(event) => {
            setConfirm(event.target.value.replace(/\D/g, ''))
          }}
        />
        <Button type="submit" variant="secondary" disabled={!canSave}>
          {save.isPending ? 'Salvando…' : lock?.configured ? 'Trocar PIN' : 'Salvar PIN'}
        </Button>
      </form>
      {confirm !== '' && pin !== confirm ? <span className={styles.warn}>Os dois campos estão diferentes.</span> : null}
      {save.isError ? (
        <span className={styles.warn} role="alert">
          ⚠️ {errorMessage(save.error, 'Não foi possível salvar o PIN.')}
        </span>
      ) : null}

      <div className={styles.row}>
        <Button
          type="button"
          variant="secondary"
          leadingIcon={<LockKeyhole />}
          disabled={!lock?.configured || stopped || unlock.isPending}
          onClick={() => {
            unlock.mutate()
          }}
        >
          {unlock.isPending ? 'Desbloqueando…' : 'Desbloquear agora'}
        </Button>
        {lock?.configured ? (
          <Button
            type="button"
            variant="ghost"
            disabled={remove.isPending}
            onClick={() => {
              remove.mutate()
            }}
          >
            Apagar PIN
          </Button>
        ) : null}
        {unlock.data ? (
          <span className={unlock.data.ok ? styles.ok : styles.warn} role="status">
            {unlock.data.ok ? '✅' : '⚠️'} {unlock.data.detail}
          </span>
        ) : null}
        {unlock.isError ? (
          <span className={styles.warn} role="alert">
            ⚠️ {errorMessage(unlock.error, 'Não foi possível desbloquear agora.')}
          </span>
        ) : null}
        {remove.isError ? (
          <span className={styles.warn} role="alert">
            ⚠️ {errorMessage(remove.error, 'Não foi possível apagar o PIN.')}
          </span>
        ) : null}
      </div>

      <span className={styles.hint}>
        Use com o celular à vista: o botão digita o PIN salvo uma vez e, se destravar, libera a automação para fazer
        o mesmo sozinha. Um erro para a automação até você conferir de novo, e no 3º erro seguido o Prism para de tentar até
        um PIN novo ser salvo. No aparelho, deixe desligada a redefinição automática de fábrica e ligue o bloqueio
        imediato ao apagar a tela.
      </span>
    </div>
  )
}
